/*!

=========================================================
* Paper Kit React - v1.3.0
=========================================================

* Product Page: https://www.creative-tim.com/product/paper-kit-react

* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/paper-kit-react/blob/main/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";
import { Link, useLocation } from "react-router-dom";
// nodejs library that concatenates strings
import classnames from "classnames";

// reactstrap components
import {
  Collapse,
  NavbarBrand,
  Navbar,
  NavItem,
  NavLink,
  Nav,
  Container,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { SERVICES } from "views/Services/services-config.js";
import { RESOURCES_PUBLISHED } from "views/Resources/resources-config.js";

import BrandMark from "../BrandMark/BrandMark";

// The switcher's three options, in the order they are offered. Kept as data so
// the "is this the one showing" check is written once rather than once per
// button. The codes match what i18n.js resolves to — it normalises every
// browser locale to exactly one of these, so plain equality is enough.
const LANGUAGES = [
  { code: "en", nameKey: "language.english", shortKey: "language.shortEnglish" },
  { code: "zh-Hans", nameKey: "language.simplified", shortKey: "language.shortSimplified" },
  { code: "zh-Hant", nameKey: "language.traditional", shortKey: "language.shortTraditional" },
];

function GeneNavbar() {
  const { i18n, t } = useTranslation();
  const [navbarColor, setNavbarColor] = React.useState("navbar-transparent");
  const [navbarCollapse, setNavbarCollapse] = React.useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = React.useState(false);
  const location = useLocation();

  const isDesktop = React.useCallback(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth >= 992;
  }, []);

  const toggleServicesDropdown = React.useCallback(() => {
    setServicesDropdownOpen((prev) => !prev);
  }, []);

  const openServicesDropdown = React.useCallback(() => {
    if (isDesktop()) setServicesDropdownOpen(true);
  }, [isDesktop]);

  const closeServicesDropdown = React.useCallback(() => {
    if (isDesktop()) setServicesDropdownOpen(false);
  }, [isDesktop]);

  // Close services dropdown when mobile navbar collapses so the menu
  // does not overflow/escape the collapsing container.
  React.useEffect(() => {
    if (!navbarCollapse) {
      setServicesDropdownOpen(false);
    }
  }, [navbarCollapse]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    // The `window.localStorage` guard this used to carry was the throwing
    // expression, not a defence against it: reading the property is itself what
    // raises SecurityError when the origin has no storage access — third-party
    // cookies blocked for the site, an opaque origin. So a reader in that state
    // changed language successfully and then took a SecurityError out of the
    // click handler. The language still changes; only remembering it for next
    // time is lost, which is the right thing to drop. Same reasoning as
    // BrandMark.js, which records it at length.
    try {
      window.localStorage.setItem("geneLanguage", lng);
    } catch (err) {
      // Nothing to do: the site opens in the browser's language next visit.
    }
  };

  // resolvedLanguage is what i18next actually rendered with, which is what the
  // reader is looking at; `language` is what was requested and can still be a
  // tag that fell back. They agree today, so this is belt and braces.
  const currentLanguage = i18n.resolvedLanguage || i18n.language;

  // Three labels sitting side by side said nothing about which of them the page
  // was already in, or that they were choosable at all. The chosen one now
  // takes a filled pill and the other two step back, and the hairline round the
  // set groups them so the pair that is not chosen reads as the alternatives.
  // aria-pressed carries the same state for a screen reader, which cannot see
  // the pill.
  const languageSwitcher = (
    <NavItem className="d-flex align-items-center gene-lang-switcher">
      <span className="mr-2 gene-lang-label">{t("language.label")}:</span>
      <span
        className="gene-lang-options"
        role="group"
        aria-label={t("language.selector")}
      >
        {LANGUAGES.map(({ code, nameKey, shortKey }) => {
          const isCurrent = currentLanguage === code;
          return (
            <Button
              key={code}
              aria-label={t(nameKey)}
              aria-pressed={isCurrent}
              color="link"
              className={classnames("gene-lang-option", {
                "is-current": isCurrent,
              })}
              onClick={() => changeLanguage(code)}
            >
              {t(shortKey)}
            </Button>
          );
        })}
      </span>
    </NavItem>
  );

  const renderNavItems = (className, onClick, isDesktopNav = false) => {
    const isServicesActive = SERVICES.some((s) => location.pathname === `/${s.slug}`);
    // Desktop uses hover; mobile uses click. Give desktop a no-op toggle so
    // its document-click listener never interferes with the mobile dropdown.
    const dropdownToggle = isDesktopNav ? () => {} : toggleServicesDropdown;
    
    return (
      <Nav navbar className={className} aria-label="Main navigation">
        <NavItem>
          <NavLink
            aria-current={location.pathname === "/index" ? "page" : undefined}
            className={classnames({
              "nav-link-active": location.pathname === "/index",
            })}
            to="/index"
            tag={Link}
            onClick={onClick}
          >
            {t("nav.home")}
          </NavLink>
        </NavItem>
        <Dropdown
          nav
          inNavbar
          isOpen={servicesDropdownOpen}
          toggle={dropdownToggle}
          onMouseEnter={openServicesDropdown}
          onMouseLeave={closeServicesDropdown}
        >
          <DropdownToggle
            aria-expanded={servicesDropdownOpen}
            aria-haspopup={true}
            caret
            color="default"
            data-toggle="dropdown"
            href="#"
            nav
            onClick={(e) => e.preventDefault()}
            role="button"
            className={classnames({
              "nav-link-active": isServicesActive,
            })}
          >
            {t("nav.services")}
          </DropdownToggle>
          <DropdownMenu className="dropdown-services" right>
            {SERVICES.map((s) => (
              <DropdownItem
                key={s.key}
                to={`/${s.slug}`}
                tag={Link}
                onClick={onClick}
                className={classnames({
                  active: location.pathname === `/${s.slug}`,
                })}
              >
                {t(`landing.service.${s.key}.title`)}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
        {/* Hidden while the page is unpublished: an unaudited page should not
            be reachable from the chrome every page wears. See
            RESOURCES_PUBLISHED in views/Resources/resources-config.js. */}
        {RESOURCES_PUBLISHED && (
        <NavItem>
          <NavLink
            aria-current={
              location.pathname === "/resources" ? "page" : undefined
            }
            className={classnames({
              "nav-link-active": location.pathname === "/resources",
            })}
            to="/resources"
            tag={Link}
            onClick={onClick}
          >
            {t("nav.resources")}
          </NavLink>
        </NavItem>
        )}
      </Nav>
    );
  };

  const toggleNavbarCollapse = () => {
    setNavbarCollapse(!navbarCollapse);
    document.documentElement.classList.toggle("nav-open");
  };

  React.useEffect(() => {
    const updateNavbarColor = () => {
      if (document.documentElement.scrollTop >= 50) {
        setNavbarColor("");
      } else if (document.documentElement.scrollTop < 50) {
        setNavbarColor("navbar-transparent");
      }
    };

    window.addEventListener("scroll", updateNavbarColor);

    return function cleanup() {
      window.removeEventListener("scroll", updateNavbarColor);
    };
  }, []);

  return (
    <Navbar
      className={classnames("fixed-top", navbarColor)}
      color-on-scroll="300"
      expand="lg"
    >
      <Container>
        <div className="navbar-translate">
          {/* No `title`: the image's alt already names the link, and a title
              alongside it only adds a second announcement of the same word.
              `data-placement` went with it — it configured a Bootstrap tooltip
              nothing in this codebase initialises. */}
          <NavbarBrand to="/index" tag={Link}>
            {/* Hover, not on arrival: the hero draws the mark when the landing
                page opens, and two of them animating at once reads as a glitch. */}
            <BrandMark className="nav-logo" alt="GeneFlou" trigger="hover" />
          </NavbarBrand>
          <Nav className="d-lg-none ml-auto mr-2" navbar>
            {languageSwitcher}
          </Nav>
          <button
            aria-expanded={navbarCollapse}
            className={classnames("navbar-toggler navbar-toggler", {
              toggled: navbarCollapse,
            })}
            onClick={toggleNavbarCollapse}
          >
            <span className="navbar-toggler-bar bar1" />
            <span className="navbar-toggler-bar bar2" />
            <span className="navbar-toggler-bar bar3" />
          </button>
        </div>
        <Collapse
          className="justify-content-end"
          navbar
          isOpen={navbarCollapse}
        >
          {/* Desktop Navigation Links */}
          {renderNavItems("d-none d-lg-flex align-items-center mr-3", undefined, true)}
          <Nav navbar className="d-none d-lg-flex">
            {languageSwitcher}
          </Nav>
          {/* Mobile Navigation Links (in drawer) */}
          {renderNavItems("d-lg-none", toggleNavbarCollapse, false)}
        </Collapse>
      </Container>
    </Navbar>
  );
}

export default GeneNavbar;
